import React from 'react';
import heroImage from '../assets/article-glp1-muscle-mass.png';
import articleBodyImage from '../assets/article-glp1.png';
import articleClinicalImage from '../assets/article-glp2.png';

const ArticleGlp1MuscleMass = () => {
  return (
    <div className="bg-white pb-16">
      <section
        className="relative isolate min-h-[38vh] w-full overflow-hidden sm:min-h-[40vh]"
        aria-labelledby="glp1-article-hero-heading"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${heroImage})` }}
          aria-hidden
        />
        <div
          className="pointer-events-none absolute inset-0 bg-gradient-to-b from-slate-950/55 via-slate-950/35 to-slate-950/60"
          aria-hidden
        />
        <div className="relative z-10 mx-auto flex min-h-[38vh] max-w-6xl flex-col items-center justify-center px-4 py-12 sm:min-h-[40vh] sm:px-6 sm:py-14 lg:px-8">
          <div className="w-full max-w-3xl text-center">
            <h1
              id="glp1-article-hero-heading"
              className="text-pretty text-3xl font-semibold tracking-tight text-white sm:text-4xl lg:text-5xl"
            >
              GLP-1 agonists and muscle mass: what the data say
            </h1>
            <p className="mt-4 text-sm text-slate-100/95 sm:text-base">
              <span className="text-slate-100">By Dr. Rahul Khanna, PhD</span>
              <span className="mx-2 text-slate-300/90" aria-hidden>
                ·
              </span>
              <time dateTime="2026-04-03" className="tabular-nums text-slate-100/90">
                April 3, 2026
              </time>
            </p>
          </div>
        </div>
      </section>

      <section
        className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8 lg:py-12"
        aria-labelledby="glp1-article-body-heading"
      >
        <div className="grid grid-cols-1 gap-8 md:grid-cols-[minmax(0,1.08fr)_minmax(0,0.92fr)] md:items-stretch md:gap-8 lg:gap-10">
          <div className="min-h-0 w-full md:h-full">
            <div className="relative aspect-[4/3] w-full overflow-hidden rounded-2xl shadow-lg ring-1 ring-slate-200/80 md:aspect-auto md:h-full md:min-h-0">
              <img
                src={articleBodyImage}
                alt="Healthcare professional in white gloves finishing care at a patient's inner elbow in a busy clinic setting"
                className="absolute inset-0 h-full w-full object-cover"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
          <div className="min-h-0 min-w-0 space-y-5 text-base leading-relaxed text-slate-600">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-brand-700">Trials and measurements</p>
              <h2
                id="glp1-article-body-heading"
                className="mt-2 text-pretty text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl"
              >
                Lean mass in context
              </h2>
            </div>
            <p>
              Weight loss with GLP-1 receptor agonists often includes lean mass as well as fat. The clinical importance
              of that loss depends on baseline body composition, age, physical activity, and how weight change was
              measured in trials.
            </p>
            <p>
              This article surveys published trial and imaging substudy data, highlights gaps (especially long follow-up
              in older adults), and separates speculation from what has actually been shown.
            </p>
            <p>
              “Lean mass” on a body-composition report is not the same thing as contractile muscle on a biopsy. Tools
              such as bioimpedance or even dual-energy X-ray absorptiometry bundle water, glycogen, and viscera with
              skeletal muscle, and rapid weight loss can shift hydration in ways that look like steeper lean loss than
              true muscle fibre change.
            </p>
            <p>
              Trials rarely standardise resistance training, protein targets, or bone-health monitoring across sites,
              yet those factors dominate real-world muscle outcomes. Until longer studies tie composition changes to
              strength, falls, and fractures, the safest read is cautious: track function and nutrition with your team,
              not only the number on a scan printout.
            </p>
          </div>
        </div>
      </section>

      <section
        className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8 lg:py-12"
        aria-labelledby="glp1-article-populations-heading"
      >
        <div className="grid grid-cols-1 gap-8 md:grid-cols-[minmax(0,0.92fr)_minmax(0,1.08fr)] md:items-stretch md:gap-8 lg:gap-10">
          <div className="min-h-0 min-w-0 space-y-5 text-base leading-relaxed text-slate-600">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-brand-700">Life-stage planning</p>
              <h2
                id="glp1-article-populations-heading"
                className="mt-2 text-pretty text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl"
              >
                Pregnancy plans, ageing, and follow-up that fits your life
              </h2>
            </div>
            <p>
              If pregnancy is possible, bring contraception and family-planning questions to routine visits. Medicines in
              this class are generally stopped before planned conception, with washout timelines and alternatives
              tailored to your label, comorbidities, and local guidance—not one-size-fits-all advice from a headline.
            </p>
            <p>
              Older adults and people with frailty may still benefit from obesity treatment, but muscle reserve and fall
              risk deserve explicit review: strength testing, gait, bone health, and whether allied-health support should
              sit alongside pharmacotherapy rather than after weight has already moved.
            </p>
            <p>
              Weight choices often intersect with caregiving and household routines. Naming those constraints early helps
              your team set realistic nutrition and resistance-training plans that you can sustain while muscle and
              metabolic numbers are monitored together.
            </p>
          </div>
          <div className="min-h-0 w-full md:h-full">
            <div className="relative aspect-[4/3] w-full overflow-hidden rounded-2xl shadow-lg ring-1 ring-slate-200/80 md:aspect-auto md:h-full md:min-h-0">
              <img
                src={articleClinicalImage}
                alt="An adult hand gently cradling a newborn baby's small hand on soft striped bedding"
                className="absolute inset-0 h-full w-full object-cover"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ArticleGlp1MuscleMass;
